import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import { validateAgentKb } from 'services/agentKb';

import { AgentKbContext } from './AgentKbProvider';

// System-KB validation, lifted out of the old KnowledgeBaseTab. The first fetch
// is triggered by the FIRST CONSUMER (via useKbValidation), not by the provider
// itself, so a user reading account notes never pays for it. Once fetched it is
// shared by the artefact page footer and the validation drawer.

interface KbValidationContextType {
  validation: AgentKb.ValidationResult | null;
  isValidating: boolean;
  revalidate: () => Promise<void>;
  ensureFetched: () => void;
}

export const KbValidationContext = createContext<KbValidationContextType>({
  validation: null,
  isValidating: false,
  revalidate: () => {
    throw new Error('revalidate used outside KbValidationProvider');
  },
  ensureFetched: () => {
    throw new Error('ensureFetched used outside KbValidationProvider');
  },
});

// Consuming this hook is what triggers the (one-time) first validation fetch.
export const useKbValidation = (): KbValidationContextType => {
  const context = useContext(KbValidationContext);
  useEffect(() => {
    context.ensureFetched();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return context;
};

const KbValidationProvider = ({ children }: { children: ReactNode }) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const { isLoading } = useContext(AgentKbContext);

  const [validation, setValidation] = useState<AgentKb.ValidationResult | null>(
    null,
  );
  const [isValidating, setIsValidating] = useState<boolean>(false);
  // A consumer has asked for validation at least once.
  const [wanted, setWanted] = useState<boolean>(false);
  const hasFetchedRef = useRef<boolean>(false);

  const ensureFetched = useCallback(() => setWanted(true), []);

  useEffect(() => {
    let isActive = true;
    if (!wanted || isLoading || hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    validateAgentKb(api).then((result) => {
      if (isActive && result.ok) {
        setValidation(result.data.validation);
      }
    });
    return () => {
      isActive = false;
    };
  }, [api, wanted, isLoading]);

  const revalidate = useCallback(async () => {
    setIsValidating(true);
    const result = await validateAgentKb(api);
    setIsValidating(false);
    if (result.ok) {
      setValidation(result.data.validation);
    } else {
      showToast(result.error);
    }
  }, [api, showToast]);

  const value = useMemo(
    () => ({ validation, isValidating, revalidate, ensureFetched }),
    [validation, isValidating, revalidate, ensureFetched],
  );

  return (
    <KbValidationContext.Provider value={value}>
      {children}
    </KbValidationContext.Provider>
  );
};

export default KbValidationProvider;
